/* START_MODULE_CONTRACT
 * PURPOSE: Frontend UI module: DeleteUserDialog.
 * SCOPE: React component or page.
 * DEPENDS: M-FE-API, M-FE-AUTH
 * LINKS: M-FE-ADMIN
 * END_MODULE_CONTRACT
 *
 * START_MODULE_MAP
 * DeleteUserDialog - Frontend UI module: DeleteUserDialog.
 * END_MODULE_MAP
 *
 * START_CHANGE_SUMMARY
 * LAST_CHANGE: 2026-07-02 - GRACE coverage: контракты функций, парные END_METHOD, блоки/логи для логики.
 * END_CHANGE_SUMMARY
 */

import { useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';

/* START_METHOD_DeleteUserDialog */
/* START_CONTRACT: DeleteUserDialog
 * PURPOSE: Диалог подтверждения удаления пользователя.
 * INPUTS: { open: boolean, onClose: ()=>void, onConfirm: (id)=>Promise, user: object }.
 * OUTPUTS: { JSX.Element|null - диалог }.
 * SIDE_EFFECTS: вызов onConfirm (HTTP наверх); локальный state.
 * LINKS: M-FE-ADMIN
 * END_CONTRACT: DeleteUserDialog
 */
export default function DeleteUserDialog({ open, onClose, onConfirm, user }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  if (!user) return null;

  /* START_METHOD_handleConfirm */
  /* START_CONTRACT: handleConfirm
   * PURPOSE: Удалить пользователя; при успехе закрыть, иначе показать ошибку.
   * INPUTS: {}.
   * OUTPUTS: { Promise<void> }.
   * SIDE_EFFECTS: onConfirm (HTTP); setLoading/setError; onClose.
   * LINKS: M-FE-ADMIN
   * END_CONTRACT: handleConfirm
   */
  const handleConfirm = async () => {
    setLoading(true);
    setError('');
    try {
      await onConfirm(user.id);
      /* START_BLOCK_DELETE_OK */
      onClose();
      /* END_BLOCK_DELETE_OK */
    } catch (err) {
      /* START_BLOCK_DELETE_FAIL */
      console.warn('[DeleteUserDialog][handleConfirm][DELETE_FAIL] delete user failed', {
        status: err.response?.status,
      });
      setError(err.response?.data?.message || 'Ошибка удаления пользователя');
      /* END_BLOCK_DELETE_FAIL */
    } finally {
      setLoading(false);
    }
  };
  /* END_METHOD_handleConfirm */

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontSize: '1rem' }}>Удаление пользователя</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 1 }}>
            {error}
          </Alert>
        )}
        <Typography variant="body2">
          Вы действительно хотите удалить пользователя &laquo;{user.email}&raquo;?
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          variant="contained"
          color="error"
          size="small"
          onClick={handleConfirm}
          disabled={loading}
        >
          Удалить
        </Button>
        <Button size="small" onClick={onClose} disabled={loading}>
          Отмена
        </Button>
      </DialogActions>
    </Dialog>
  );
}
/* END_METHOD_DeleteUserDialog */
